import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { MdOutlineDelete, MdEdit } from "react-icons/md";
import { baseApiURL } from "../../baseUrl";
import { FiBook, FiCalendar, FiUsers, FiGitBranch, FiFilter, FiAlertCircle } from "react-icons/fi";

const Subjects = ({ branch: lockedBranchName }) => {
  const [data, setData] = useState({ 
    name: "", 
    code: "", 
    total: "", 
    semester: "",
    branch: "",
    regulation: ""
  });
  const [editData, setEditData] = useState(null);
  const [selected, setSelected] = useState("add");
  const [subject, setSubject] = useState([]);
  const [filteredSubjects, setFilteredSubjects] = useState([]);
  const [branches, setBranches] = useState([]);
  const [noStudentsMessage, setNoStudentsMessage] = useState("");
  const [studentRegulations, setStudentRegulations] = useState([]);
  
  // Filter states
  const [filters, setFilters] = useState({
    branch: "",
    semester: "",
    regulation: ""
  });

  // Semester options
  const semesters = [1, 2, 3, 4, 5, 6, 7, 8];

  useEffect(() => {
    getSubjectHandler();
    getBranchesHandler();
    getStudentRegulationsHandler();
  }, []);

  const getStudentRegulationsHandler = () => {
    axios
      .get(`${baseApiURL()}/student/details/getRegulations`)
      .then((res) => {
        if (res.data.success) {
          setStudentRegulations(res.data.regulations || []);
        }
      })
      .catch((err) => console.error("Error fetching student regulations:", err));
  };

  // Apply filters whenever subjects or filters change
  useEffect(() => {
    filterSubjects();
  }, [subject, filters]);

  useEffect(() => {
    checkStudentPresence();
  }, [filters.semester, filters.branch, branches]);

  const checkStudentPresence = async () => {
    if (!filters.semester) {
      setNoStudentsMessage("");
      return;
    }

    try {
      const payload = { semester: Number(filters.semester) };
      if (filters.branch) {
        const branchObj = branches.find(b => b._id === filters.branch);
        if (branchObj?.name) {
          payload.branch = branchObj.name;
        }
      }

      const response = await axios.post(`${baseApiURL()}/student/details/getDetails`, payload);
      if (response.data.success && response.data.user && response.data.user.length > 0) {
        setNoStudentsMessage("");
      } else {
        setNoStudentsMessage("no students are there for that semester");
      }
    } catch (error) {
      setNoStudentsMessage("no students are there for that semester");
    }
  };

  const getBranchesHandler = () => {
    axios
      .get(`${baseApiURL()}/branch/getBranch`)
      .then((response) => {
        if (response.data.success) {
          setBranches(response.data.branches);
          if (lockedBranchName) {
            const matchedBranch = response.data.branches.find(b => b.name === lockedBranchName);
            if (matchedBranch) {
              setData(prev => ({ ...prev, branch: matchedBranch._id }));
              setFilters(prev => ({ ...prev, branch: matchedBranch._id }));
            }
          }
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        toast.error(error.message);
      });
  };

  const getSubjectHandler = () => {
    axios
      .get(`${baseApiURL()}/subject/getSubject`)
      .then((response) => {
        if (response.data.success) {
          setSubject(response.data.subject);
          setFilteredSubjects(response.data.subject); // Initially show all
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        toast.error(error.message);
      });
  };

  // Filter subjects based on selected branch and semester
  const filterSubjects = () => {
    let filtered = [...subject];
    
    if (filters.branch) {
      filtered = filtered.filter(item => {
        // Handle both populated branch object and branch ID
        const branchId = item.branch?._id || item.branch;
        return branchId === filters.branch;
      });
    }
    
    if (filters.semester) {
      filtered = filtered.filter(item => 
        String(item.semester) === String(filters.semester)
      );
    }

    if (filters.regulation) {
      filtered = filtered.filter(item => 
        item.regulation === filters.regulation
      );
    }
    
    setFilteredSubjects(filtered);
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      branch: "",
      semester: "",
      regulation: ""
    });
  };

  const addSubjectHandler = () => {
    if (!data.name || !data.code || !data.total || !data.semester || !data.branch || !data.regulation) {
      toast.error("Please fill all fields");
      return;
    }
    toast.loading("Adding Subject...");
    axios
      .post(`${baseApiURL()}/subject/addSubject`, data)
      .then((response) => {
        toast.dismiss();
        if (response.data.success) {
          toast.success(response.data.message);
          setData({ name: "", code: "", total: "", semester: "", branch: "", regulation: "" });
          getSubjectHandler();
          setSelected("view");
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        toast.dismiss();
        console.error("Error:", error);
        toast.error(error.response?.data?.message || error.message);
      });
  };

  const deleteSubjectHandler = (id, name) => {
    const confirmDelete = window.confirm(`Are you sure you want to delete subject "${name}"?`);
    if (!confirmDelete) return;

    toast.loading("Deleting Subject...");
    axios
      .delete(`${baseApiURL()}/subject/deleteSubject/${id}`)
      .then((response) => {
        toast.dismiss();
        if (response.data.success) {
          toast.success(response.data.message);
          getSubjectHandler();
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        toast.dismiss();
        toast.error(error.response?.data?.message || error.message);
      });
  };

  const updateSubjectHandler = () => {
    if (!editData.name || !editData.code || !editData.total || !editData.semester || !editData.branch || !editData.regulation) {
      toast.error("Please fill all fields");
      return;
    }

    toast.loading("Updating Subject...");
    axios
      .put(`${baseApiURL()}/subject/updateSubject/${editData._id}`, editData)
      .then((response) => {
        toast.dismiss();
        if (response.data.success) {
          toast.success(response.data.message);
          setEditData(null);
          setSelected("view");
          getSubjectHandler();
        } else {
          toast.error(response.data.message);
        }
      })
      .catch((error) => {
        toast.dismiss();
        toast.error(error.response?.data?.message || error.message);
      });
  };

  const resetForm = () => {
    setData({ name: "", code: "", total: "", semester: "", branch: "", regulation: "" });
  };

  // Get branch name by ID
  const getBranchName = (branchId) => {
    const branch = branches.find(b => b._id === branchId);
    return branch ? branch.name : 'N/A';
  };

  return (
    <div className="w-full space-y-6">
      {/* Header Banner */}
      <div className="bento-header-banner flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-black tracking-tight text-slate-900 dark:text-white">Subject Management</h1>
          <p className="text-xs md:text-sm text-slate-500 dark:text-slate-400 mt-1">Configure subjects, total hours & regulations</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bento-card p-1.5 bg-slate-100 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl">
        <div className="grid grid-cols-2 gap-1.5">
          <button
            className={`px-4 py-2.5 rounded-xl font-bold text-xs transition-all cursor-pointer ${
              selected === "add"
                ? "bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-xs border border-slate-200 dark:border-slate-700"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-slate-800/50"
            }`}
            onClick={() => setSelected("add")}
          >
            <div className="flex items-center justify-center space-x-2">
              <FiBook className="w-4 h-4" />
              <span>Add Subject</span>
            </div>
          </button>

          <button
            className={`px-4 py-2.5 rounded-xl font-bold text-xs transition-all cursor-pointer ${
              selected === "view"
                ? "bg-white dark:bg-slate-800 text-indigo-600 dark:text-indigo-400 shadow-xs border border-slate-200 dark:border-slate-700"
                : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white hover:bg-white/50 dark:hover:bg-slate-800/50"
            }`}
            onClick={() => setSelected("view")}
          >
            <div className="flex items-center justify-center space-x-2">
              <FiUsers className="w-4 h-4" />
              <span>View Subjects</span>
            </div>
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xs border border-slate-200 dark:border-slate-800 p-6 md:p-8">
        {selected === "add" && (
          <div className="max-w-2xl mx-auto space-y-6">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <FiBook className="text-indigo-600 dark:text-indigo-400 text-lg" />
                <h2 className="text-base font-bold text-slate-900 dark:text-white">Subject Information</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="code" className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                    Subject Code *
                  </label>
                  <input
                    type="text"
                    id="code"
                    required
                    value={data.code}
                    onChange={(e) => setData({ ...data, code: e.target.value.toUpperCase() })}
                    className="w-full px-3.5 py-2.5 text-xs bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g., CS101"
                  />
                </div>
                <div>
                  <label htmlFor="name" className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                    Subject Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    required
                    value={data.name}
                    onChange={(e) => setData({ ...data, name: e.target.value })}
                    className="w-full px-3.5 py-2.5 text-xs bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="e.g., Data Structures"
                  />
                </div>
                <div>
                  <label htmlFor="branch" className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                    Branch *
                  </label>
                  <select
                    id="branch"
                    required
                    value={data.branch}
                    onChange={(e) => setData({ ...data, branch: e.target.value })}
                    disabled={!!lockedBranchName}
                    className={`w-full px-3.5 py-2.5 text-xs bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-xl ${lockedBranchName ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <option value="">Select Branch</option>
                    {branches.map((branch) => (
                      <option key={branch._id} value={branch._id}>
                        {branch.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="semester" className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                    Semester *
                  </label>
                  <select
                    id="semester"
                    required
                    value={data.semester}
                    onChange={(e) => setData({ ...data, semester: e.target.value })}
                    className="w-full px-3.5 py-2.5 text-xs bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-xl"
                  >
                    <option value="">Select Semester</option>
                    {semesters.map((sem) => (
                      <option key={sem} value={sem}>
                        Semester {sem}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="regulation" className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                    Regulation *
                  </label>
                  <select
                    id="regulation"
                    required
                    value={data.regulation}
                    onChange={(e) => setData({ ...data, regulation: e.target.value })}
                    className="w-full px-3.5 py-2.5 text-xs bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-xl"
                  >
                    <option value="">Select Regulation</option>
                    {(studentRegulations.length > 0 ? studentRegulations : ["R24", "R23", "R22", "R20"]).map((reg) => (
                      <option key={reg} value={reg}>
                        {reg}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="total" className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                    Total Classes *
                  </label>
                  <input
                    type="number"
                    id="total"
                    required
                    min="1"
                    value={data.total}
                    onChange={(e) => setData({ ...data, total: e.target.value })}
                    className="w-full px-3.5 py-2.5 text-xs bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-xl"
                    placeholder="e.g., 45"
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-slate-100 dark:border-slate-800">
              <button
                type="button"
                onClick={resetForm}
                className="px-5 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 font-bold text-xs transition-all cursor-pointer"
              >
                Reset Form
              </button>
              <button
                type="button"
                onClick={addSubjectHandler}
                className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold text-xs transition-all shadow-md shadow-indigo-600/20 cursor-pointer"
              >
                Add Subject
              </button>
            </div>
          </div>
        )}

        {selected === "view" && (
          <div className="space-y-6">
            {/* Filters Section */}
            <div className="p-5 bg-slate-50 dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-4">
              <div className="flex items-center space-x-2">
                <FiFilter className="text-indigo-600 dark:text-indigo-400 text-base" />
                <h3 className="text-xs font-bold text-slate-900 dark:text-white uppercase tracking-wider">Filter Subjects</h3>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="filterBranch" className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                    Filter by Branch
                  </label>
                  <select
                    id="filterBranch"
                    value={filters.branch}
                    onChange={(e) => setFilters({ ...filters, branch: e.target.value })}
                    disabled={!!lockedBranchName}
                    className={`w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-white dark:bg-slate-900 text-slate-900 dark:text-white ${lockedBranchName ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <option value="">All Branches</option>
                    {branches.map((branch) => (
                      <option key={branch._id} value={branch._id}>
                        {branch.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="filterSemester" className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                    Filter by Semester
                  </label>
                  <select
                    id="filterSemester"
                    value={filters.semester}
                    onChange={(e) => setFilters({ ...filters, semester: e.target.value })}
                    className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                  >
                    <option value="">All Semesters</option>
                    {semesters.map((sem) => (
                      <option key={sem} value={sem}>
                        Semester {sem}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="filterRegulation" className="block text-xs font-bold text-slate-600 dark:text-slate-400 mb-1">
                    Filter by Regulation
                  </label>
                  <select
                    id="filterRegulation"
                    value={filters.regulation}
                    onChange={(e) => setFilters({ ...filters, regulation: e.target.value })}
                    className="w-full p-2 text-xs border border-slate-200 dark:border-slate-800 rounded-xl bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
                  >
                    <option value="">All Regulations</option>
                    {(studentRegulations.length > 0 ? studentRegulations : Array.from(new Set(subject.map(s => s.regulation).filter(Boolean)))).map((reg) => (
                      <option key={reg} value={reg}>
                        {reg}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-slate-200 dark:border-slate-800 text-xs">
                <span className="text-slate-500 dark:text-slate-400 font-semibold">
                  Showing <strong className="text-slate-900 dark:text-white">{filteredSubjects.length}</strong> of {subject.length} subjects
                </span>
                <button
                  onClick={clearFilters}
                  className="px-3 py-1.5 bg-slate-200 dark:bg-slate-800 text-slate-700 dark:text-slate-300 font-bold text-xs rounded-xl hover:bg-slate-300 dark:hover:bg-slate-700 transition cursor-pointer"
                >
                  Clear Filters
                </button>
              </div>

              {noStudentsMessage && (
                <div className="p-2 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800 rounded-xl text-amber-800 dark:text-amber-300 text-xs flex items-center space-x-2">
                  <FiAlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                  <span className="font-medium">{noStudentsMessage}</span>
                </div>
              )}
            </div>

            {/* Subjects List */}
            <div className="space-y-3">
              {filteredSubjects.length > 0 ? (
                filteredSubjects.map((item) => (
                  <div
                    key={item._id}
                    className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-2xl p-4 hover:border-indigo-500/50 transition-all flex flex-col sm:flex-row sm:items-center justify-between gap-4"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
                        <FiBook className="text-xl" />
                      </div>
                      <div>
                        <h3 className="text-sm font-bold text-slate-900 dark:text-white">
                          <span className="font-mono text-indigo-600 dark:text-indigo-400 mr-2">{item.code}</span>
                          {item.name}
                        </h3>
                        <div className="flex flex-wrap items-center gap-3 mt-1 text-xs text-slate-500 dark:text-slate-400">
                          <div className="flex items-center space-x-1">
                            <FiGitBranch className="text-emerald-500" />
                            <span>{item.branch?.name || getBranchName(item.branch)}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <FiCalendar className="text-blue-500" />
                            <span>Semester {item.semester}</span>
                          </div>
                          <span className="bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800 px-2 py-0.5 rounded-lg text-[10px] font-bold">
                            {item.regulation}
                          </span>
                          <div className="flex items-center space-x-1">
                            <FiUsers className="text-purple-500" />
                            <span>{item.total} Classes</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 self-end sm:self-center">
                      <button
                        className="p-2 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-slate-800 rounded-xl transition cursor-pointer"
                        onClick={() => {
                          setEditData(item);
                          setSelected("edit");
                        }}
                        title="Edit Subject"
                      >
                        <MdEdit size={18} />
                      </button>
                      <button
                        className="p-2 text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/50 rounded-xl transition cursor-pointer"
                        onClick={() => deleteSubjectHandler(item._id, item.name)}
                        title="Delete Subject"
                      >
                        <MdOutlineDelete size={18} />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 text-slate-400">
                  <FiBook className="w-12 h-12 mx-auto mb-2 text-slate-300 dark:text-slate-700" />
                  <h3 className="text-sm font-bold text-slate-700 dark:text-slate-300">No Subjects Found</h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    {subject.length === 0 
                      ? "Add your first subject to get started" 
                      : "No subjects match the selected filters"}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {selected === "edit" && editData && (
          <div className="max-w-2xl mx-auto space-y-6">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <FiBook className="text-indigo-600 dark:text-indigo-400 text-lg" />
                <h2 className="text-base font-bold text-slate-900 dark:text-white">Edit Subject Information</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="editCode" className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                    Subject Code *
                  </label>
                  <input
                    type="text"
                    id="editCode"
                    required
                    value={editData.code}
                    onChange={(e) => setEditData({ ...editData, code: e.target.value.toUpperCase() })}
                    className="w-full px-3.5 py-2.5 text-xs bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter subject code"
                  />
                </div>
                <div>
                  <label htmlFor="editName" className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                    Subject Name *
                  </label>
                  <input
                    type="text"
                    id="editName"
                    required
                    value={editData.name}
                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                    className="w-full px-3.5 py-2.5 text-xs bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    placeholder="Enter subject name"
                  />
                </div>
                <div>
                  <label htmlFor="editBranch" className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                    Branch *
                  </label>
                  <select
                    id="editBranch"
                    required
                    value={editData.branch?._id || editData.branch || ""}
                    onChange={(e) => setEditData({ ...editData, branch: e.target.value })}
                    disabled={!!lockedBranchName}
                    className={`w-full px-3.5 py-2.5 text-xs bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-xl ${lockedBranchName ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <option value="">Select Branch</option>
                    {branches.map((branch) => (
                      <option key={branch._id} value={branch._id}>
                        {branch.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="editSemester" className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                    Semester *
                  </label>
                  <select
                    id="editSemester"
                    required
                    value={editData.semester}
                    onChange={(e) => setEditData({ ...editData, semester: e.target.value })}
                    className="w-full px-3.5 py-2.5 text-xs bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-xl"
                  >
                    <option value="">Select Semester</option>
                    {semesters.map((sem) => (
                      <option key={sem} value={sem}>
                        Semester {sem}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="editRegulation" className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                    Regulation *
                  </label>
                  <select
                    id="editRegulation"
                    required
                    value={editData.regulation}
                    onChange={(e) => setEditData({ ...editData, regulation: e.target.value })}
                    className="w-full px-3.5 py-2.5 text-xs bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-xl"
                  >
                    <option value="">Select Regulation</option>
                    {(studentRegulations.length > 0 ? studentRegulations : ["R24", "R23", "R22", "R20"]).map((reg) => (
                      <option key={reg} value={reg}>
                        {reg}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="editTotal" className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-1.5">
                    Total Classes *
                  </label>
                  <input
                    type="number"
                    id="editTotal"
                    required
                    min="1"
                    value={editData.total}
                    onChange={(e) => setEditData({ ...editData, total: e.target.value })}
                    className="w-full px-3.5 py-2.5 text-xs bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-slate-900 dark:text-white rounded-xl"
                    placeholder="Enter total classes"
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end space-x-3 pt-4 border-t border-slate-100 dark:border-slate-800">
              <button
                type="button"
                onClick={() => {
                  setEditData(null);
                  setSelected("view");
                }}
                className="px-5 py-2.5 border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 font-bold text-xs transition cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={updateSubjectHandler}
                className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold text-xs transition shadow-md shadow-indigo-600/20 cursor-pointer"
              >
                Update Subject
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Subjects;